import { LinkButton } from '@euler/app/components/LinkButton';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsMobileLayout } from '@euler/functions/responsiveness';
import { useAlert } from '@euler/functions/ux/useAlert';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { memo, useCallback, useContext } from 'react';
import {
  ActivityIndicator,
  Linking,
  Modal,
  Platform,
  StyleProp,
  StyleSheet,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { MediasPageContext } from '../context';
import { useDownloadMedias } from '../functions/useDownloadMedias';
import { getMediasPageTitleByQueryParams } from '../functions/utils';

export const SelectMedias = memo(
  ({ style }: { style?: StyleProp<ViewStyle> }) => {
    const { rpx } = useResponsiveContext();

    const alert = useAlert();

    const {
      selection$,
      isSelectionModeActive$,
      selectAll,
      deselectAll,
      data,
      queryParams,
    } = useContext(MediasPageContext);

    const [selection] = useBehaviorSubject(selection$);

    const isAllSelected =
      selection.length > 0 && selection.length === data.length;

    const [isSelectionModeActive] = useBehaviorSubject(isSelectionModeActive$);

    const isMobile = useIsMobileLayout();
    const textStyle: TextStyle = {
      fontSize: rpx(isMobile ? 12 : 13),
    };

    const onEnterSelectionMode = useCallback(() => {
      isSelectionModeActive$.next(true);
    }, [isSelectionModeActive$]);

    const onToggleSelectAll = useCallback(() => {
      if (isAllSelected) {
        deselectAll();
      } else {
        selectAll();
      }
    }, [deselectAll, isAllSelected, selectAll]);

    const onCancel = useCallback(() => {
      isSelectionModeActive$.next(false);
      deselectAll();
    }, [deselectAll, isSelectionModeActive$]);

    const { downloadMedias, downloadStats } = useDownloadMedias({
      zipFilename: [
        queryParams.licensePlateNo,
        getMediasPageTitleByQueryParams(queryParams),
      ].join('_'),
      onDownloadComplete: args => {
        void alert({
          title: args.failed > 0 ? `` : '下载完成!',
        });
        onCancel();
      },
      onPermissionDenied: async message => {
        await alert({
          title: '下载失败',
          message,
        });
        if (Platform.OS !== 'web') {
          await Linking.openSettings();
        }
      },
      onDownloadError: e => {
        const msg = e instanceof Error ? e.message : '下载失败!';
        void alert({
          title: msg.length > 10 ? '下载失败' : msg,
          message: msg.length > 10 ? msg : undefined,
        });
      },
    });

    const downloadSelected = useCallback(async () => {
      if (!selection.length) return;
      await downloadMedias(selection);
    }, [downloadMedias, selection]);

    const downloadAll = useCallback(async () => {
      if (!data.length) return;
      await downloadMedias(data);
    }, [data, downloadMedias]);

    if (!data.length) {
      return null;
    }

    if (!isSelectionModeActive) {
      return (
        <LinkButton text="选择" style={style} onPress={onEnterSelectionMode} />
      );
    }

    return (
      <>
        <StackLayout
          direction="row"
          alignItems="center"
          spacing={rpx(isMobile ? 5 : 8)}
          style={style}
        >
          <LinkButton
            text={isAllSelected ? '取消全选' : '全选'}
            textStyle={textStyle}
            onPress={onToggleSelectAll}
          />
          <LinkButton
            text={`下载已选 (${selection.length})`}
            textStyle={textStyle}
            disabled={!selection.length}
            onPress={downloadSelected}
          />
          <LinkButton
            text="全部下载"
            textStyle={textStyle}
            onPress={downloadAll}
          />
          <LinkButton text="取消" textStyle={textStyle} onPress={onCancel} />
        </StackLayout>
        <Modal
          animationType="none"
          transparent={true}
          visible={downloadStats.downloading}
        >
          <Center
            style={[
              StyleSheet.absoluteFill,
              {
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
              },
            ]}
          >
            <ActivityIndicator size="large" style={{ marginBottom: rpx(15) }} />
            <StackLayout direction="row">
              <Typography.Text color="white">正在下载... </Typography.Text>
              <Typography.Text color="white">
                {`${downloadStats.progress}/${downloadStats.total}`}
              </Typography.Text>
            </StackLayout>
          </Center>
        </Modal>
      </>
    );
  },
);
